import { useAuth } from "@/_core/hooks/useAuth";
import { useRoute } from "wouter";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { trpc } from "@/lib/trpc";
import { Loader2, AlertCircle, Eye, CheckCircle, Clock, TrendingUp, Download, FileText } from "lucide-react";
import { toast } from "sonner";
import { exportToCSV, exportToExcel } from "@/lib/exportUtils";
import type { ContactSubmission } from "@/lib/exportUtils";

const AdminDashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const [, params] = useRoute("/admin");
  const [selectedStatus, setSelectedStatus] = useState<string | undefined>(undefined);
  const [selectedSubmission, setSelectedSubmission] = useState<number | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  // Redirect if not admin
  useEffect(() => {
    if (!authLoading && (!user || user.role !== "admin")) {
      window.location.href = "/";
      toast.error("You do not have permission to access this page");
    }
  }, [user, authLoading, params]);

  const listQuery = trpc.contact.list.useQuery({
    limit: 20,
    offset: 0,
    status: selectedStatus,
  });

  const getByIdQuery = trpc.contact.getById.useQuery(
    { id: selectedSubmission! },
    { enabled: selectedSubmission !== null }
  );

  const updateStatusMutation = trpc.contact.updateStatus.useMutation({
    onSuccess: () => {
      toast.success("Status updated successfully");
      listQuery.refetch();
      setSelectedSubmission(null);
    },
    onError: (error) => {
      toast.error(error.message || "Failed to update status");
    },
  });

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!user || user.role !== "admin") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card>
          <CardContent className="pt-6">
            <p className="text-muted-foreground">Access denied. Admin only.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleNavigateHome = () => {
    window.location.href = "/";
  };

  const handleExportCSV = async () => {
    if (!listQuery.data || !listQuery.data.submissions || listQuery.data.submissions.length === 0) {
      toast.error("No submissions to export");
      return;
    }

    try {
      setIsExporting(true);
      const submissions: ContactSubmission[] = listQuery.data.submissions.map((sub: any) => ({
        id: sub.id,
        name: sub.name,
        email: sub.email,
        phone: sub.phone,
        apartmentType: sub.apartmentType,
        inquiryType: sub.inquiryType,
        message: sub.message,
        visitDate: sub.visitDate,
        status: sub.status,
        createdAt: sub.createdAt,
        updatedAt: sub.updatedAt,
      }));
      exportToCSV(submissions);
      toast.success(`Exported ${submissions.length} submissions as CSV`);
    } catch (error) {
      toast.error("Failed to export CSV");
      console.error(error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportExcel = async () => {
    if (!listQuery.data || !listQuery.data.submissions || listQuery.data.submissions.length === 0) {
      toast.error("No submissions to export");
      return;
    }

    try {
      setIsExporting(true);
      const submissions: ContactSubmission[] = listQuery.data.submissions.map((sub: any) => ({
        id: sub.id,
        name: sub.name,
        email: sub.email,
        phone: sub.phone,
        apartmentType: sub.apartmentType,
        inquiryType: sub.inquiryType,
        message: sub.message,
        visitDate: sub.visitDate,
        status: sub.status,
        createdAt: sub.createdAt,
        updatedAt: sub.updatedAt,
      }));
      exportToExcel(submissions);
      toast.success(`Exported ${submissions.length} submissions as Excel`);
    } catch (error) {
      toast.error("Failed to export Excel");
      console.error(error);
    } finally {
      setIsExporting(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "new":
        return "bg-blue-100 text-blue-800";
      case "contacted":
        return "bg-yellow-100 text-yellow-800";
      case "converted":
        return "bg-green-100 text-green-800";
      case "spam":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "new":
        return <Clock className="w-4 h-4" />;
      case "contacted":
        return <TrendingUp className="w-4 h-4" />;
      case "converted":
        return <CheckCircle className="w-4 h-4" />;
      default:
        return <AlertCircle className="w-4 h-4" />;
    }
  };

  const submission = getByIdQuery.data;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="container mx-auto px-4 py-8">
        {/* Navigation */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">Contact Submissions</h1>
            <p className="text-muted-foreground">Manage and track all contact form submissions</p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportCSV}
              disabled={isExporting || !listQuery.data || !listQuery.data.submissions || listQuery.data.submissions.length === 0}
              className="gap-2"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportExcel}
              disabled={isExporting || !listQuery.data || !listQuery.data.submissions || listQuery.data.submissions.length === 0}
              className="gap-2"
            >
              <FileText className="w-4 h-4" />
              Export Excel
            </Button>
            <Button variant="outline" onClick={handleNavigateHome}>Back to Home</Button>
          </div>
        </div>


        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Filters */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Filters</CardTitle>
              </CardHeader>
              <CardContent>
                <Select value={selectedStatus || "all"} onValueChange={(value) => setSelectedStatus(value === "all" ? undefined : value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Submissions</SelectItem>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="converted">Converted</SelectItem>
                    <SelectItem value="spam">Spam</SelectItem>
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>

            {/* Submissions List */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">
                  Submissions ({listQuery.data?.submissions?.length || 0})
                </CardTitle>
                <CardDescription>
                  {listQuery.isLoading ? "Loading..." : `Showing ${listQuery.data?.submissions?.length || 0} submissions`}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {listQuery.isLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                  </div>
                ) : listQuery.data && listQuery.data.submissions && listQuery.data.submissions.length > 0 ? (
                  <div className="space-y-3">
                    {listQuery.data.submissions.map((sub: any) => (
                      <div
                        key={sub.id}
                        onClick={() => setSelectedSubmission(sub.id)}
                        className={`p-4 border rounded-lg cursor-pointer transition ${
                          selectedSubmission === sub.id
                            ? "border-blue-500 bg-blue-50"
                            : "border-slate-200 hover:border-slate-300"
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="font-semibold text-foreground">{sub.name}</p>
                            <p className="text-sm text-muted-foreground">{sub.email}</p>
                            <p className="text-sm text-muted-foreground mt-1">{sub.inquiryType}</p>
                          </div>
                          <Badge className={getStatusColor(sub.status)}>
                            {getStatusIcon(sub.status)}
                            <span className="ml-1">{sub.status}</span>
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">No submissions found</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Detail Panel */}
          <div className="lg:col-span-1">
            {selectedSubmission ? (
              getByIdQuery.isLoading ? (
                <Card>
                  <CardContent className="pt-6 flex items-center justify-center">
                    <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                  </CardContent>
                </Card>
              ) : submission ? (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Submission Details</CardTitle>
                    <CardDescription>ID: {submission.id}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Contact Info */}
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">Contact Information</p>
                      <div className="space-y-2">
                        <p className="text-sm">
                          <span className="font-semibold">Name:</span> {submission.name}
                        </p>
                        <p className="text-sm">
                          <span className="font-semibold">Email:</span>{" "}
                          <a href={`mailto:${submission.email}`} className="text-blue-600 hover:underline">
                            {submission.email}
                          </a>
                        </p>
                        <p className="text-sm">
                          <span className="font-semibold">Phone:</span>{" "}
                          <a href={`tel:${submission.phone}`} className="text-blue-600 hover:underline">
                            {submission.phone}
                          </a>
                        </p>
                      </div>
                    </div>

                    {/* Submission Info */}
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">Submission Information</p>
                      <div className="space-y-2">
                        <p className="text-sm">
                          <span className="font-semibold">Apartment Type:</span> {submission.apartmentType}
                        </p>
                        <p className="text-sm">
                          <span className="font-semibold">Inquiry Type:</span> {submission.inquiryType}
                        </p>
                        {submission.visitDate && (
                          <p className="text-sm">
                            <span className="font-semibold">Preferred Visit Date:</span> {submission.visitDate}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Message */}
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">Message</p>
                      <p className="text-sm bg-slate-100 p-3 rounded text-foreground whitespace-pre-wrap">
                        {submission.message}
                      </p>
                    </div>

                    {/* Status */}
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">Status</p>
                      {submission && (
                        <Select
                          value={submission.status || "new"}
                          onValueChange={(newStatus) => {
                            updateStatusMutation.mutate({
                              id: submission.id,
                              status: newStatus as any,
                            });
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new">New</SelectItem>
                            <SelectItem value="contacted">Contacted</SelectItem>
                            <SelectItem value="converted">Converted</SelectItem>
                            <SelectItem value="spam">Spam</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </div>

                    {/* Timestamps */}
                    <div className="pt-4 border-t border-slate-200 space-y-2">
                      <p className="text-xs text-muted-foreground">
                        <span className="font-semibold">Submitted:</span>{" "}
                        {new Date(submission.createdAt).toLocaleString()}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        <span className="font-semibold">Updated:</span>{" "}
                        {new Date(submission.updatedAt).toLocaleString()}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-muted-foreground">Failed to load submission details</p>
                  </CardContent>
                </Card>
              )
            ) : (
              <Card>
                <CardContent className="pt-6">
                  <p className="text-muted-foreground text-center">Select a submission to view details</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
